import { type Option, RowKey, type RowLocalId, Timescale } from "@tento-lona/sheets";
import type { SheetViewModel } from "@tento-lona";
import { CellGroup } from "@tento-ui/components/sheet/cell-group";
import { SheetCell } from "@tento-ui/components/sheet/sheet-cell";
import type { SheetLayoutPort } from "@tento-ui/components/sheet/sheet-layout";
import { SpanCell } from "@tento-ui/components/sheet/span-cell";
import type { BindGeneration } from "@tento-scroll/engine/scroll-engine";
import { sheetColumnsOf } from "./sheet-columns-of.ts";
import type { SheetBinder } from "./sheet-index-renderer.ts";

/**
 * The sheet element's geometry rebinds (F11): after a layout change (a row
 * resize, an all-day expand, a same-id settle), re-apply the new row heights
 * and offsets to what is already mounted — the column cells, the
 * span-stacked fixed children, the row-options anchor, the static children
 * and the fixed children's y. No content is rebound. Moved out of
 * sheet-element.ts unchanged; the element hands in what they read, read
 * when a rebind runs.
 */

/** What the rebinds read from their sheet element (read when a rebind runs). */
export type GeometryRebindHost = {
  /** The bound sheet, if any. */
  readonly sheet: Option<SheetViewModel.Surface<SheetBinder>>;
  /** The layout surface the sheet drives. */
  readonly layout: SheetLayoutPort;
  /** The element's current bind generation (cells from an older one are skipped). */
  readonly bindGeneration: BindGeneration;
  /** The row whose options popover is open, if any. */
  readonly activeRowOptions: Option<RowLocalId>;
  /** The open row-options anchor, if any (its height follows its row). */
  readonly rowOptionsAnchor: Option<HTMLElement>;
  /** Dismiss a static child from the header and body scroll ports. */
  dismissStaticChild(staticId: string): void;
  /** The element's static-child y position for a row. */
  staticChildYPosition(rowId: RowLocalId, rowLayout: SheetCell.Layout): Option<{ px: number; anchor: "top" | "bottom" }>;
  /** A fixed child's top for a row at `offsetPx` in `location` (`SheetElement.Layout.bodyFixedChildTop`). */
  bodyFixedChildTop(offsetPx: number, location: string, sheet: SheetViewModel.Surface<SheetBinder>): number;
};

/**
 * The row each mounted cell of a column paints, in cell order (`null`: none),
 * by the identity its bind stamped on it (`rsid` serial, else `rid` wire) as
 * `placeCells` reads it — never by slot: a windowed column mounts only the
 * resident rows, so slot `i` is not `flattened.rows[i]` once rows above the
 * window are not resident (a reorder while scrolled down then sized every
 * cell with the row above it's height until a scroll re-bound them).
 */
function rowsOfCells<R extends CellRowIndex.Row>(
  $cells: readonly Pick<Element, "getAttribute">[],
  index: CellRowIndex<R>,
): Option<R>[] {
  return $cells.map(($cell) => index.rowOf($cell));
}

/**
 * The rows by the identities a bind stamps on a cell (`rsid` serial, `rid`
 * wire), built once per rebind over every flattened row and shared by all
 * the rendered columns, not rebuilt per column.
 */
class CellRowIndex<R extends CellRowIndex.Row> {
  readonly #bySerial: Map<string, R>;
  readonly #byWire: Map<string, R>;

  constructor(rows: readonly R[]) {
    this.#bySerial = new Map(rows.map((row) => [row.id.serial, row] as const));
    this.#byWire = new Map(rows.map((row) => [row.id.toString(), row] as const));
  }

  /** The row `$cell` paints, by its serial, else its wire id; `null`: none. */
  rowOf($cell: Pick<Element, "getAttribute">): Option<R> {
    const serial = $cell.getAttribute("rsid");
    const wire = $cell.getAttribute("rid");
    return (serial == null ? null : this.#bySerial.get(serial)) ?? (wire == null ? null : this.#byWire.get(wire)) ?? null;
  }
}

namespace CellRowIndex {
  /** What the index reads of a row: its serial and wire ids. */
  export type Row = { readonly id: { readonly serial: string; toString(): string } };
}

/** Re-apply row heights to the mounted cells, span-stacked fixed children and the row-options anchor. */
function cellHeights(host: GeometryRebindHost): void {
  const sheet = host.sheet;
  if (!sheet) return;

  const index = new CellRowIndex(sheet.joinedTree.flattened.rows);
  for (const el of host.layout.columns.renderedElements()) {
    const { $header, $body, $footer } = sheetColumnsOf(el);
    const $cells: SheetCell[] = [
      ...$header.$cells(),
      ...$body.$cells(),
      ...($footer?.$cells() ?? []),
    ];
    const rows = rowsOfCells($cells, index);
    for (const [idx, $cell] of $cells.entries()) {
      // Stretch guard: skip cells whose CONTENT is from a superseded generation.
      // Applying the new sheet's height to a cell still holding the previous
      // sheet's content stretches stale content for 1+ frames (CLS). Those cells
      // get height+content bound atomically by the drain. Same-generation cells
      // (drag-resize, all-day expand, same-id settle) match and are unaffected.
      if ($cell.boundGeneration !== host.bindGeneration) continue;
      const row = rows[idx];
      if (!row) {
        continue;
      }
      const rowLayout = sheet.layout.getRowLayout(row.id);
      if (!rowLayout) continue;
      $cell.bindHeight(rowLayout.cellHeightPx);

      if (!rowLayout.stackingInfo) continue;
      $cell.bindStackingConfig(rowLayout.stackingInfo);
    }
  }

  // Update span-stacked fixed children heights (same sync path as stacking above)
  {
    const fixedChildren = host.layout.children.fixed.getAllFixedChildren();
    if (fixedChildren) {
      for (const $child of fixedChildren) {
        if (!($child instanceof CellGroup)) continue;
        const owner = $child.getAttribute("data-fixed-owner");
        if (owner !== "span-stacked") continue;
        const rowIdAttr = $child.getAttribute("row-id");
        if (!rowIdAttr) continue;
        const rowId = sheet.ids.resolve(RowKey.of(rowIdAttr));
        const rowLayout = sheet.layout.getRowLayout(rowId);
        if (!rowLayout) continue;
        const row = sheet.joinedTree.flattened.rows.find((r) => r.id === rowId);
        if (!row) continue;
        const spec = row.layoutSpec(sheet.timescale);
        if (!spec.minWidthTimescale) continue;
        const spanCount = Timescale.conservativeSpanCount(
          spec.minWidthTimescale,
          sheet.timescale,
        );
        const subcellHeightPx = Math.floor(
          rowLayout.cellHeightPx / spanCount,
        );
        $child.bindConfig({
          individualCellHeight: subcellHeightPx,
          visibleCellCount: spanCount,
          seeMoreHeight: 0,
          totalCells: spanCount,
        });
        $child.style.height = rowLayout.cellHeightPx + "px";
      }
    }
  }

  // Sync RowOptionsAnchor height if active
  const activeRowId = host.activeRowOptions;
  if (!activeRowId) return;
  const $anchor = host.rowOptionsAnchor;
  if (!$anchor) return;
  const rowLayout = sheet.layout.rowLayout.get(activeRowId);
  if (!rowLayout) return;
  $anchor.style.height = `${rowLayout.cellHeightWithChildrenPx}px`;
}

/** Re-position the static children for their rows' layout; dismiss those whose row left. */
function staticChildPositions(host: GeometryRebindHost): void {
  const sheet = host.sheet;
  if (!sheet) return;

  const $allStaticChildren = host.layout.children.static.getAll();

  for (const $child of $allStaticChildren) {
    const rowIdAttr = $child.getAttribute("data-row-id");
    if (!rowIdAttr) continue;
    const rowId = sheet.ids.resolve(RowKey.of(rowIdAttr));

    const rowLayout = sheet.layout.getRowLayout(rowId);
    if (!rowLayout) {
      const staticId = $child.getAttribute("data-static-id");
      if (staticId) host.dismissStaticChild(staticId);
      continue;
    }

    const yPos = host.staticChildYPosition(rowId, rowLayout);
    if (yPos == null) continue;

    if (yPos.anchor === "bottom") {
      $child.style.top = "auto";
      $child.style.bottom = `${yPos.px}px`;
    } else {
      $child.style.bottom = "auto";
      $child.style.top = `${yPos.px}px`;
    }

    // Set height on spanned static children (CellGroup.Row).
    // Span-stacked rows use fixedChildren instead, not staticChildren.
    if ($child instanceof CellGroup.Row) {
      const nextHeight = rowLayout.cellHeightWithChildrenPx + "px";
      if ($child.style.height !== nextHeight) {
        $child.style.height = nextHeight;
      }
      $child.bindHeight(rowLayout.cellHeightPx);
    }
  }
}

/** Re-apply the fixed children's y (and a span cell's height) for their rows; dismiss those whose row left. */
function fixedChildYPositions(host: GeometryRebindHost): void {
  const sheet = host.sheet;
  if (!sheet) return;

  const $allFixedChildren = host.layout.children.fixed.getAllFixedChildren();

  for (const $child of $allFixedChildren) {
    const rowIdAttr = $child.getAttribute("row-id");
    if (!rowIdAttr) continue;
    const rowId = sheet.ids.resolve(RowKey.of(rowIdAttr));

    const rowLayout = sheet.layout.rowLayout.get(rowId);
    if (!rowLayout) {
      const identifier = $child.id;
      if (identifier) {
        host.layout.children.fixed.dismiss(identifier, false);
      }
      continue;
    }

    const location = sheet.joinedTree.getRowLocation(rowId) ?? "body";

    // Body fixed children clear the header the same way `--body-margin-top`
    // does; footer offsets are pre-rebased to footer-relative by SheetLayoutSpec.make.
    const yPx = host.bodyFixedChildTop(
      rowLayout.offsetPx,
      location,
      sheet,
    );

    // Include the stored inset value that was set when the fixed child was revealed
    const insetTop = Number.parseFloat(
      $child.getAttribute("data-inset-top") ?? "0",
    );
    const totalY = yPx + insetTop;

    const nextAdditionalY = totalY + "px";
    if ($child.style.getPropertyValue("--additional-y") !== nextAdditionalY) {
      $child.style.setProperty("--additional-y", nextAdditionalY);
    }

    // Update height for SpanCell fixed children during drag resize
    if ($child instanceof SpanCell) {
      $child.style.setProperty("--height", rowLayout.cellHeightPx + "px");
      const $innerCell = $child.pluginCell;
      if ($innerCell instanceof SheetCell) {
        $innerCell.bindHeight(rowLayout.cellHeightPx);
      }
    }
  }
}

/** The geometry rebinds the sheet element runs (see the module note). */
export namespace SheetGeometryRebinds {
  export const rebindCellHeights = cellHeights;
  export const rebindStaticChildPositions = staticChildPositions;
  export const rebindFixedChildYPositions = fixedChildYPositions;
  /** The row each of `$cells` paints among `rows` (see `rowsOfCells`). */
  export function cellRows<R extends { readonly id: { readonly serial: string; toString(): string } }>(
    $cells: readonly Pick<Element, "getAttribute">[],
    rows: readonly R[],
  ): Option<R>[] {
    return rowsOfCells($cells, new CellRowIndex(rows));
  }
}
